import React, {useCallback, useEffect, useRef, useState} from 'react';
import Screen from "../components/Screen";
import Title from "../components/Title";
import tickSVG from '../images/tick.svg';
import Box, {Direction} from "../components/Box";
import LayoutWideElementNextToNarrow from "../components/LayoutWideElementNextToNarrow";
import Icon from "../components/Icon";
import icons from "../css/Icon.module.css";
import Button from "../components/Button";
import PhoneNumberInput from "../components/PhoneNumberInput";
import TextInput from "../components/TextInput";
import textStyles from "../css/Text.module.css";
import {IDeeplinkStore} from "../stores/DeeplinkStore";
import {observer} from "mobx-react";
import {rem} from "../utils/CSSUtils";
import agent from "../agent";
import {formatI18nKeys, toI18N} from "../utils/I18n";

// @ts-ignore
import CopyToClipboard from "react-copy-to-clipboard";
import {isMobile} from "react-device-detect";
import {ellipsisIfNeeded} from "../utils/TextUtils";
import Alert, {AlertRefProps} from "../components/Alert";
import LoadingSpinner from "../components/LoadingSpinner";
import {showInfoToast, usePrevious} from "../utils/HooksHelper";
import Modal from "../components/Modal";
import * as Analytics from "../utils/Analytics";

const smsSnackText = toI18N('generate_link_send_sms_success');
const emailSnackText = toI18N('generate_link_send_email_success');

interface Props {
    store: IDeeplinkStore,
    generateDeeplink: Function,
    groupName: string,
    defaultCountryCode: any,
    fontColor: string,
    groupId: string,
    isActive: boolean
}

function GenerateLinkScreen(props: Props) {

    // Props

    const {
        generateDeeplink,
        groupId,
        groupName,
        fontColor,
        store: {
            emailCanProceed,
            isLoading,
            linkId,
            url,
            urlQrCode,
            textToShare,
            email,
            setEmail
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_link_generate_link_page);
        }
    });


    // Ref

    const alertRef = useRef<AlertRefProps>(null);
    const [showQrCode, setShowQrCode] = useState(false);

    // Effects

    useEffect(() => {
        generateDeeplink();
        // eslint-disable-next-line
    }, []); // mimic componentDidMount

    // Callbacks

    const info = useCallback(
        (text) => {
            showInfoToast(alertRef, text);
        },
        [alertRef],
    );
    const error = useCallback(
        (text) => {
            alertRef && alertRef.current && alertRef.current.error(text);
        },
        [alertRef],
    );

    const handleSendEmailInvitation = useCallback(
        async () => {
            try {
                await agent.Invitation.sendEmailInvitation(email, groupId, url);
                info(emailSnackText);
                setEmail("")
            } catch (e) {
                if (e.timeout) {
                    error(toI18N("general_timeout_error"));
                    return
                }
                console.error(e)
            }
        },
        [error, setEmail, email, groupId, url, info],
    );

    let title;
    if (linkId) {
        title = isLoading ?
            toI18N('generate_link_saving_the_link') :
            toI18N('generate_link_link_saved');
    } else {
        title = isLoading ?
            toI18N('generate_link_loading_title') :
            formatI18nKeys('generate_link_done_title', ellipsisIfNeeded(groupName, 24));
    }

    return (
        <Screen
            title={"generate_link_screen_header_title"}
            subtitle={"generate_link_screen_header_subtitle"}
            hideBack
            hideNext>

            <Alert ref={alertRef}/>

            <Box center sideMargin shrink style={{marginTop: isMobile ? 65 : 120}}>
                <Title title={title}/>
            </Box>

            <Box center grow>
                {!isLoading && <img style={{height: rem(68), width: rem(68)}} src={tickSVG} alt={"Ready"}/>}
                {isLoading && <LoadingSpinner xl/>}
            </Box>

            <Box center direction={Direction.Column} sideMargin bottomMargin={isMobile ? 20 : 60} shrink>

                <Box center bottomMargin={16}>
                    <Title xs title={toI18N('generate_link_subtitle')}/>
                </Box>
                <Box direction={Direction.Column} center bottomMargin={8}>
                    <label htmlFor={'text-to-share-generate-link-screen'} className={textStyles.label}>
                        {toI18N('generate_link_text_to_share_title')}
                    </label>
                    <LayoutWideElementNextToNarrow
                        firstChild={
                            <>
                                <textarea id={'text-to-share-generate-link-screen'}
                                          readOnly
                                          cols={5}
                                          style={{height: rem(74), width: '100%'}}
                                          value={textToShare}/>
                            </>
                        }
                        secondChild={
                            // @ts-ignore
                            !isMobile || !navigator.share ?

                                <CopyToClipboardButton
                                    text={textToShare}
                                    disabled={isLoading}
                                    onCopy={info.bind(null, toI18N('link_was_copied'))}/> :

                                <Button primary disabled={isLoading} xs onClick={async () => {
                                    const shareData = {
                                        title: toI18N('generate_link_text_to_share_title'),
                                        text: textToShare
                                    };

                                    // @ts-ignore
                                    await navigator.share(shareData)
                                }}>
                                    <Icon white xs name={icons.iconShare}/>
                                </Button>
                        }
                    />
                </Box>
                <Box direction={Direction.Column} center bottomMargin={8}>
                    <label htmlFor={'email-generate-link-screen'} className={textStyles.label}>
                        {toI18N('generate_link_send_invitation_email')}
                    </label>
                    <LayoutWideElementNextToNarrow
                        firstChild={
                            <TextInput
                                inputMode={"email"}
                                autoCapitalize={'off'}
                                autoCorrect={'off'}
                                onChange={setEmail}
                                value={email}
                                readonly={isLoading}
                                id={"email-generate-link-screen"}
                                placeholder={toI18N('enter_email_input_placeholder')}/>
                        }
                        secondChild={
                            <Button primary onClick={handleSendEmailInvitation} disabled={isLoading || !emailCanProceed}
                                    xs>
                                <Icon white xs name={icons.iconSend}/>
                            </Button>
                        }
                    />
                </Box>

                <Box center>
                    <Button onClick={setShowQrCode.bind(null, true)} maxWidth={250} primary>
                        {toI18N('show_qr_code')}
                    </Button>
                </Box>

                <Box direction={Direction.Column} center>
                    <label htmlFor={'copy-link-generate-link-screen'} className={textStyles.label}>
                        {toI18N('generate_link_copy_direct_link')}
                    </label>
                    <LayoutWideElementNextToNarrow
                        firstChild={
                            <TextInput value={url} readonly id={"copy-link-generate-link-screen"} label={undefined}/>
                        }
                        secondChild={
                            <CopyToClipboardButton
                                text={url}
                                onCopy={info.bind(null, toI18N('link_was_copied'))}
                                disabled={isLoading}
                            />
                        }
                    />
                </Box>

            </Box>

            <Modal darkBackdrop onDismiss={setShowQrCode.bind(null, false)}
                id={"loading-modal-detail-screen"} center show={showQrCode}>
                <img alt={'url qr code'} style={{border: `6px solid ${fontColor}`, borderRadius: "8px"}} src={urlQrCode}/>
            </Modal>

        </Screen>
    );
}

interface CopyToClipboardProps {
    text: string,
    disabled: boolean,
    onCopy: () => void
}

function CopyToClipboardButton(props: CopyToClipboardProps) {
    return (
        <CopyToClipboard text={props.text}>
            <Button primary disabled={props.disabled} xs onClick={() => {
                props.onCopy();
            }}>
                <Icon xs white name={icons.iconCopy}/>
            </Button>
        </CopyToClipboard>
    );
}

export default observer(GenerateLinkScreen);
